import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const ACCENT = "#FF385C";
const ACCENT_DARK = "#E61E4D";
const AMBER = "#F59E0B";

// Self-contained phone mockup that embeds the live Welcome Book demo.
// Replicates the home-page ProductShowcase phone, lazy-loaded on scroll.
export const WelcomeBookPhonePreview = ({
  iframeSrc,
  iframeTitle = "Demo Welcome Book Hostland",
  heading = "Prova il Welcome Book dal vivo",
  sub,
  liveDemoLink,
  liveDemoLabel = "Guarda in live",
}) => {
  const phoneRef = useRef(null);
  const screenRef = useRef(null);
  const [iframeReady, setIframeReady] = useState(false);

  const DEVICE_W = 390;
  const DEVICE_H = Math.round((DEVICE_W * 956) / 440);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    if (!iframeSrc || !phoneRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setIframeReady(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px 0px" }
    );
    io.observe(phoneRef.current);
    return () => io.disconnect();
  }, [iframeSrc]);

  useEffect(() => {
    if (!iframeSrc) return;
    const el = screenRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / DEVICE_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [iframeSrc, iframeReady]);

  if (!iframeSrc) return null;

  return (
    <section
      className="px-4 sm:px-6 py-12 md:py-20 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #EFF6FF 0%, #FFFFFF 40%, #FEF3C7 100%)" }}
      data-testid="seo-welcome-book-demo"
    >
      <div aria-hidden className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-2xl opacity-25 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${ACCENT}55, transparent 70%)` }} />
      <div aria-hidden className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full blur-2xl opacity-20 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${AMBER}55, transparent 70%)` }} />

      <div className="max-w-6xl mx-auto relative">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-zinc-900 tracking-tight mb-3 leading-tight">
            {heading}
          </h2>
          {sub && (
            <p className="text-zinc-500 font-medium text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
              {sub}
            </p>
          )}
        </div>

        <div className="flex flex-col items-center">
          <div className="mb-5 md:mb-7">
            <span className="inline-flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full bg-zinc-900 text-white text-[12px] sm:text-[13px] font-bold shadow-xl shadow-black/25 border border-white/10">
              <span className="relative flex h-3.5 w-3.5 items-center justify-center">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/70" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
              </span>
              <span className="tracking-tight">Provala qui — usala come se fosse tua</span>
            </span>
          </div>

          <div
            ref={phoneRef}
            className="relative mx-auto w-full max-w-[340px] sm:max-w-[380px] lg:max-w-[440px] xl:max-w-[460px]"
            data-testid="seo-demo-phone"
          >
            <div aria-hidden className="absolute inset-0 -m-10 rounded-[60px] blur-3xl opacity-50 pointer-events-none"
              style={{ background: `radial-gradient(circle at 50% 45%, ${ACCENT}45, transparent 62%)` }} />

            <div
              className="relative mx-auto"
              style={{ aspectRatio: "440/956", transform: "rotate(-2.4deg)", transformOrigin: "center 60%" }}
            >
              <div
                className="absolute inset-0 rounded-[52px] p-[11px] sm:p-[13px]"
                style={{
                  background: "linear-gradient(150deg,#42424a 0%,#0a0a0c 46%,#2a2a30 100%)",
                  boxShadow: `0 40px 90px -20px ${ACCENT}40, 0 60px 150px -55px rgba(0,0,0,0.65), inset 0 1.5px 0 rgba(255,255,255,0.16), inset 0 0 0 1.5px rgba(255,255,255,0.05)`,
                }}
              >
                <div aria-hidden className="absolute -left-[3px] top-[22%] w-[3px] h-9 rounded-l bg-zinc-700/90" />
                <div aria-hidden className="absolute -left-[3px] top-[33%] w-[3px] h-14 rounded-l bg-zinc-700/90" />
                <div aria-hidden className="absolute -right-[3px] top-[27%] w-[3px] h-20 rounded-r bg-zinc-700/90" />

                <div ref={screenRef} className="relative w-full h-full rounded-[42px] overflow-hidden bg-black">
                  <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-30 w-[30%] h-6 rounded-full bg-black flex items-center justify-end px-2.5 shadow-inner">
                    <span className="w-2 h-2 rounded-full bg-zinc-900 ring-1 ring-zinc-700/70" />
                  </div>
                  {!(iframeReady && scale > 0) && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-gradient-to-b from-zinc-50 to-white">
                      <div className="w-7 h-7 rounded-full border-2 border-zinc-200 border-t-zinc-500 animate-spin" />
                      <span className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-400">Demo live</span>
                    </div>
                  )}
                  {iframeReady && scale > 0 && (
                    <div
                      className="absolute top-0 left-0"
                      style={{ width: DEVICE_W, height: DEVICE_H, transform: `scale(${scale})`, transformOrigin: "top left" }}
                    >
                      <iframe
                        src={iframeSrc}
                        title={iframeTitle}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                        className="border-0 bg-white block"
                        style={{ width: DEVICE_W, height: DEVICE_H }}
                        data-testid="seo-demo-live-iframe"
                      />
                    </div>
                  )}
                  <div aria-hidden className="absolute inset-0 pointer-events-none z-20"
                    style={{ background: "linear-gradient(120deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 26%, rgba(255,255,255,0) 72%, rgba(255,255,255,0.06) 100%)" }} />
                </div>
              </div>
            </div>
          </div>

          {liveDemoLink && (
            <div className="mt-7 md:mt-9 flex flex-col items-center gap-2">
              <motion.a
                href={liveDemoLink}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
                className="group relative inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full font-black text-[15px] text-white shadow-[0_16px_36px_-10px_rgba(0,0,0,0.4)] overflow-hidden"
                style={{ background: `linear-gradient(105deg, ${ACCENT} 0%, ${ACCENT_DARK} 55%, ${AMBER} 100%)`, backgroundSize: "200% auto" }}
                data-testid="seo-demo-live-btn"
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-80" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
                </span>
                <span className="tracking-tight">{liveDemoLabel}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </motion.a>
              <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Nessuna registrazione · scorri e tocca · è la vera app</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WelcomeBookPhonePreview;
